// ** React Imports
import { useState } from "react";

import { Link as RouterLink, useLocation } from "react-router-dom";
import { Link } from "@mui/material";

// ** MUI Components
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";

// ** Icons Imports
import ChevronLeft from "mdi-material-ui/ChevronLeft";
import KeyOutline from "mdi-material-ui/KeyOutline";
import EmailCheckOutline from "mdi-material-ui/EmailCheckOutline";

// ** Third Party Imports
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Hooks
import { useAuth } from "src/hooks/useAuth";
import { pathForAuthRoute } from "src/@core/utils/urlQueryPreservation";

// ** Auth UI
import AuthShell from "src/views/pages/auth/AuthShell";

yup.setLocale({
  string: {
    email: "É necessário indicar um e-mail válido",
  },
  mixed: {
    required: "Este campo é obrigatório",
  },
});

const schema = yup.object().shape({
  email: yup.string().email().required(),
});

const BackToLogin = ({ search }) => (
  <Link className="sp-auth__back" component={RouterLink} to={pathForAuthRoute("/login", search)}>
    <ChevronLeft />
    <span>Voltar ao login</span>
  </Link>
);

const ForgotPasswordPage = () => {
  // ** Hooks
  const location = useLocation();
  const auth = useAuth();

  const [completed, setCompleted] = useState(false);

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    const { email } = data;
    auth.forgotPassword(
      { email },
      () => {
        setError("email", {
          type: "manual",
          message: "Oops, algo correu mal...",
        });
      },
      () => {
        setCompleted(true);
      }
    );
  };

  // ── Success state: recovery e-mail sent ────────────────────────────
  if (completed) {
    return (
      <AuthShell
        icon={<EmailCheckOutline />}
        eyebrow="Verifica o teu e-mail"
        title="Link enviado!"
        subtitle="Dentro de segundos recebes um e-mail com o link para definires uma nova senha."
      >
        <div className="sp-auth__alert">
          Procura o e-mail na tua caixa de entrada - vê também em <strong>promoções</strong> ou <strong>spam</strong>.
          O assunto é <strong>"[SalesPark] &lt;o teu nome&gt;, a tua recuperação de Senha"</strong>.
        </div>
        <p className="sp-auth__hint">
          O link é válido durante <strong>24 horas</strong>. Verifica a tua caixa de e-mail o mais rápido possível.
        </p>
        <BackToLogin search={location.search} />
      </AuthShell>
    );
  }

  // ── Request state: ask for e-mail ──────────────────────────────────
  return (
    <AuthShell
      icon={<KeyOutline />}
      eyebrow="Recuperar acesso"
      title="Esqueceste-te da senha?"
      subtitle="Indica o teu e-mail e enviamos-te um link para definires uma nova senha."
    >
      <div className="sp-auth__alert">
        Certifica-te que indicas o <strong>endereço de e-mail correto</strong>. Só recebes o e-mail de recuperação se o
        endereço for válido.
      </div>

      <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <FormControl className="sp-auth__field" fullWidth>
          <label className="sp-auth__label" htmlFor="forgot-email">
            Endereço de e-mail
          </label>
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field: { value, onChange, onBlur } }) => (
              <OutlinedInput
                autoFocus
                fullWidth
                id="forgot-email"
                value={value}
                onBlur={onBlur}
                onChange={onChange}
                error={Boolean(errors.email)}
                placeholder="o-teu-email@exemplo.com"
              />
            )}
          />
          {errors.email && <FormHelperText>{errors.email.message}</FormHelperText>}
        </FormControl>

        <Button className="sp-auth__submit" type="submit" variant="contained">
          Enviar link de recuperação
        </Button>
      </form>

      <BackToLogin search={location.search} />
    </AuthShell>
  );
};

export default ForgotPasswordPage;
